import { startHolyLoader } from "holy-loader"
import { usePathname } from "next/navigation"

import { LOCALE_NONE } from "@/modules/jobAds/client/constants"
import { usePageContext } from "@/modules/jobSearch/components/PageContext/PageContextProvider"
import { parsedUrlQueryToURLSearchParams } from "@/modules/routing/utils"

import { useJobAdsSearchParams } from "./useJobAdsSearchParams"

type PartialRecord<K extends string, T> = {
  [P in K]?: T
}

type AvailableQueryParams =
  | "abroad_only"
  | "candidacy_type"
  | "company_business_type"
  | "company_sectors"
  | "contract_duration"
  | "contract"
  | "exclusive"
  | "groupOffers"
  | "job_with_impact"
  | "languages"
  | "lat"
  | "lng"
  | "locale"
  | "localized_location"
  | "location"
  | "position_category_uuid"
  | "publication_date"
  | "q"
  | "radius"
  | "remote_types"
  | "sort"
  | "start_date"
  | "study_levels"
  | "tags"
  | "work_experience_code"

export type AvailableFilters = PartialRecord<AvailableQueryParams, string[] | string>

type UpdatedQueryParams = {
  availableFilters: AvailableFilters
  isASavedSearch?: boolean
  isAnAutocompleteSearch?: boolean
}

type UseJobAdsFilterNavigation = {
  navigateWithUpdatedQueryParams: (updatedQueryParams: UpdatedQueryParams) => void
}

export const useJobAdsFilterNavigation = (): UseJobAdsFilterNavigation => {
  const pathname = usePathname()
  const searchParams = useJobAdsSearchParams()
  const { push } = usePageContext()
  const [routePrefix] = pathname.split("/job-offers")

  const pushNavigation = (updatedSearchParams: URLSearchParams): void => {
    startHolyLoader()
    updatedSearchParams.delete("page")

    const search = updatedSearchParams.toString()
    const pushCall = push(`${routePrefix}/job-offers?${search || `locale=${LOCALE_NONE}`}`, undefined, {
      scroll: false,
      shallow: true,
    })
    const promise = pushCall instanceof Promise ? pushCall : Promise.resolve()

    promise.catch(error => {
      // workaround for https://github.com/vercel/next.js/issues/37362
      if (!error.cancelled) {
        throw error
      }
    })
  }

  const navigateWithUpdatedQueryParams = (updatedQueryParams: UpdatedQueryParams): void => {
    const { availableFilters, isASavedSearch, isAnAutocompleteSearch } = updatedQueryParams

    const currentSearch = searchParams.toString()
    const updatedSearchParams = parsedUrlQueryToURLSearchParams(availableFilters, new URLSearchParams(searchParams))

    if (isASavedSearch) {
      updatedSearchParams.set("saved_search", "true")
    } else {
      updatedSearchParams.delete("saved_search")
    }

    if (isAnAutocompleteSearch) {
      updatedSearchParams.set("autocomplete_search", "true")
    } else {
      updatedSearchParams.delete("autocomplete_search")
    }

    if (currentSearch.toString() !== updatedSearchParams.toString()) {
      pushNavigation(updatedSearchParams)
    }
  }

  return {
    navigateWithUpdatedQueryParams,
  }
}
